import { create } from 'zustand';
import { TuningPreset } from '../types/index.js';
import { TUNINGS, tuningToPreset, customTuningFromId } from '../data/tunings.js';
import { savedCustomById } from '../data/customTunings.js';

/** FRE-139: every catalogue tuning is a tuner preset (string 1 = highest). Ids are the catalogue ids. */
export const TUNING_PRESETS: TuningPreset[] = TUNINGS.map(tuningToPreset);

/** Preset for a catalogue id or a `custom:<notes>` id (saved list first, otherwise parsed from the id). */
export const presetForId = (id: string | null | undefined): TuningPreset | undefined => {
  if (!id) return undefined;
  const known = TUNING_PRESETS.find((p) => p.id === id);
  if (known) return known;
  const custom = savedCustomById(id) ?? customTuningFromId(id);
  return custom ? tuningToPreset(custom) : undefined;
};

const PRESET_KEY = 'fb-tuner-preset';
const readSavedPreset = (): TuningPreset => {
  try {
    const id = localStorage.getItem(PRESET_KEY);
    return presetForId(id) ?? TUNING_PRESETS[0];
  } catch {
    return TUNING_PRESETS[0];
  }
};

interface TunerState {
  isListening: boolean;
  selectedPreset: TuningPreset;
  selectedString: number | null; // 1 to 6/7, or null for auto-detect
  detectedNote: string | null;
  detectedOctave: number | null;
  detectedFrequency: number;
  centsDeviation: number; // -50 to +50
  isInTune: boolean;
  referenceA4: number; // default 440 Hz
  errorMessage: string | null;

  // Actions
  setIsListening: (listening: boolean) => void;
  setPreset: (preset: TuningPreset) => void;
  setSelectedString: (str: number | null) => void;
  setPitchData: (note: string, octave: number, frequency: number, cents: number) => void;
  setReferenceA4: (freq: number) => void;
  setErrorMessage: (msg: string | null) => void;
}

export const useTunerStore = create<TunerState>((set) => ({
  isListening: false,
  selectedPreset: readSavedPreset(),
  selectedString: null,
  detectedNote: null,
  detectedOctave: null,
  detectedFrequency: 0,
  centsDeviation: 0,
  isInTune: false,
  referenceA4: 440,
  errorMessage: null,

  setIsListening: (isListening) => set({ isListening }),
  setPreset: (selectedPreset) => {
    try {
      localStorage.setItem(PRESET_KEY, selectedPreset.id);
    } catch {
      /* not remembered in private mode */
    }
    set({ selectedPreset, selectedString: null });
  },
  setSelectedString: (selectedString) => set({ selectedString }),
  setPitchData: (detectedNote, detectedOctave, detectedFrequency, centsDeviation) =>
    set({
      detectedNote,
      detectedOctave,
      detectedFrequency,
      centsDeviation,
      isInTune: Math.abs(centsDeviation) <= 3
    }),
  setReferenceA4: (referenceA4) => set({ referenceA4 }),
  setErrorMessage: (errorMessage) => set({ errorMessage })
}));
